import { CustomVideoElement } from 'custom-media-element'
import { MediaTracksMixin } from 'media-tracks'
import { CastableMediaMixin } from 'castable-video/castable-mixin'
// import Hls from 'hls.js/dist/hls.mjs';
import insightsInit from '../../utils/insights'
import HTML5EventsAdapter from '../../utils/HTML5EventsAdapter'
import Hls from 'hls.js'
import isSafari from '../../utils/browserDetection'
import setMediaMetadata from '../../utils/sessionMetadata'

const HlsVideoMixin = (superclass) => {
  return class HlsVideo extends superclass {
    static shadowRootOptions = { ...superclass.shadowRootOptions }

    static getTemplateHTML = (attrs) => {
      const { src, ...rest } = attrs // eslint-disable-line no-unused-vars
      let html = superclass.getTemplateHTML(rest)
      const style = html.split('</style>')
      html = style[0] + `

        video::-webkit-media-text-track-container {
          overflow: hidden;
          padding-bottom: 0;
          z-index: 0;
          text-align: center;
          color: rgba(255, 255, 255, 1);

          letter-spacing: normal;
          word-spacing: normal;
          text-transform: none;
          text-indent: 0;
          text-decoration: none;
          pointer-events: none;
          -webkit-user-select: none;
          transform: translateY(-50px) !important;

          flex: 1 1 auto;

          -webkit-line-box-contain: block inline-box replaced;
        }

        video[controls]::-webkit-media-text-track-container.visible-controls-bar {
          height: calc(100% - var(--inline-controls-bar-height) - var(--inline-controls-inside-margin));
        }

        video::cue {
          background-color: rgba(0, 0, 0, 0.1);
        }

        video::-webkit-media-text-track-display {
          position: absolute;
          overflow: hidden;
          white-space: pre-wrap;
          box-sizing: border-box;
        }

        video::-webkit-media-text-track-display-backdrop {
          display: inline-block;
          padding: 0 0.5rem;
        }

        video::cue(:future) {
          color: gray;
        }

        video::-webkit-media-text-track-container b {
          font-weight: bold;
        }

        video::-webkit-media-text-track-container u {
          text-decoration: underline;
        }

        video::-webkit-media-text-track-container i {
          font-style: italic;
        }

        video::-webkit-media-text-track-container .hidden {
          display: none;
        }



        /* Font size for mobile devices */
        @media (max-width: 600px) {
          video::cue {
            font-size: 1rem !important;
          }

          video::-webkit-media-text-track-container{
            transform: translateY(-10%) !important;
          }

        }

        /* Font size for tablets */
        @media (min-width: 601px) and (max-width: 1024px) {
          video::cue {
            font-size: 1.4rem !important;
          }
        }
        
        @media (min-width: 1025px) and (max-width: 1919px) {
          video::cue {
            font-size: 2rem !important;
          }
        }
        

        /* Font size for monitor and big desktop range between 1920 to 3000 */
        @media (min-width: 1920px) and (max-width: 3000px) {
          video::cue {
            font-size: 2.5rem !important;
          }
        }
        

        /* Additional styling for better text rendering */
        video::cue {
          text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
          text-align: center;
        }

        </style>
      `
      html += style[1]

      return html
    }

    async checkDrmSystemSupport (system) {
      return new Promise((resolve) => {
        let config
        if (system === 'com.widevine.alpha') {
          config = [{
            audioCapabilities: [{
              contentType: 'audio/mp4;codecs="mp4a.40.2"',
              encryptionScheme: 'cbcs',
              robustness: 'SW_SECURE_CRYPTO'
            }],
            videoCapabilities: [{
              contentType: 'video/mp4;codecs="avc1.42E01E"',
              encryptionScheme: 'cbcs',
              robustness: 'SW_SECURE_CRYPTO'
            }]
          }]
        } else if (system === 'com.apple.fps') {
          config = [{
            initDataTypes: ['sinf'],
            audioCapabilities: [{ contentType: 'audio/mp4;codecs="mp4a.40.2"' }],
            videoCapabilities: [{ contentType: 'video/mp4;codecs="avc1.42E01E"' }]
          }]
        } else if (system === 'com.apple.fps.1_0') {
          config = [{
            initDataTypes: ['cenc'],
            audioCapabilities: [{ contentType: 'audio/mp4;codecs="mp4a.40.2"' }],
            videoCapabilities: [{ contentType: 'video/mp4;codecs="avc1.42E01E"' }]
          }]
        } else if (system === 'com.apple.fps.2_0') {
          config = [{
            initDataTypes: ['cenc'],
            audioCapabilities: [{ contentType: 'audio/mp4;codecs="mp4a.40.2"' }],
            videoCapabilities: [{ contentType: 'video/mp4;codecs="avc1.42E01E"' }]
          }]
        } else if (system === 'com.apple.fps.3_0') {
          config = [{
            initDataTypes: ['cenc'],
            audioCapabilities: [{ contentType: 'audio/mp4;codecs="mp4a.40.2"' }],
            videoCapabilities: [{ contentType: 'video/mp4;codecs="avc1.42E01E"' }]
          }]
        }

        try {
          navigator
            .requestMediaKeySystemAccess(system, config)
            .then((mediaKeySystemAccess) => resolve(true)).catch((e) => resolve(false))
        } catch (e) {
          console.error(e)
          return resolve(false)
        }
      })
    }

    getWidevineSettings () {
      const playerSettings = {}
      if (this.getAttribute('widevinelicenseuri') !== null && (!this.nativeEl.canPlayType('application/vnd.apple.mpegurl') || !isSafari())) {
        playerSettings.emeEnabled = true
        playerSettings.drmSystems = {
          'com.widevine.alpha': {
            licenseUrl: this.getAttribute('widevinelicenseuri')
          }
        }
        playerSettings.drmSystemOptions = {
          videoRobustness: 'SW_SECURE_CRYPTO',
          audioRobustness: 'SW_SECURE_CRYPTO'
        }
        playerSettings.requestMediaKeySystemAccessFunc = (keySystem, supportedConfigurations) => {
          let widevineOptions = null
          if (this.getAttribute('widevinev2settings') && this.getAttribute('widevinev2settings') === 'true') {
            widevineOptions = [
              {
                label: 'gumletconfig', // For debugging and/or identifying the selected config.
                initDataTypes: ['cenc', 'webm'],
                sessionTypes: ['temporary'],
                audioCapabilities: [
                // Preferred codec.
                  {
                    contentType: 'audio/mp4; codecs="mp4a.40.2"',
                    robustness: 'SW_SECURE_CRYPTO'
                  }
                ],
                videoCapabilities: [
                // Preferred codec and robustness level
                  {
                    contentType: 'video/mp4; codecs="avc1.42E01E"',
                    robustness: 'HW_SECURE_ALL'
                  },
                  {
                    contentType: 'video/mp4; codecs="avc1.42E01E"',
                    robustness: 'SW_SECURE_DECODE'
                  },
                  {
                    contentType: 'video/mp4; codecs="avc1.42E01E"',
                    robustness: 'SW_SECURE_CRYPTO'
                  }
                ]
              }
            ]
            if (this.getAttribute('widevinerobustness') === 'L1') {
              widevineOptions[0].videoCapabilities = [{
                contentType: 'video/mp4; codecs="avc1.42E01E"',
                robustness: 'HW_SECURE_ALL'
              }]
            } else if (this.getAttribute('widevinerobustness') === 'L2') {
              widevineOptions[0].videoCapabilities = [{
                contentType: 'video/mp4; codecs="avc1.42E01E"',
                robustness: 'HW_SECURE_CRYPTO'
              }]
            } else if (this.getAttribute('widevinel3robustness') !== null) {
              widevineOptions[0].videoCapabilities = [{
                contentType: 'video/mp4; codecs="avc1.42E01E"',
                robustness: 'SW_SECURE_DECODE'
              }, {
                contentType: 'video/mp4; codecs="avc1.42E01E"',
                robustness: 'SW_SECURE_CRYPTO'
              }]
            }
          } else {
            widevineOptions = [
              {
                audioCapabilities: [{ contentType: 'audio/mp4;codecs="mp4a.40.2"', encryptionScheme: 'cbcs', robustness: 'SW_SECURE_CRYPTO' }],
                videoCapabilities: [{ contentType: 'video/mp4;codecs="avc1.42E01E"', encryptionScheme: 'cbcs', robustness: 'HW_SECURE_ALL' }]
              },
              {
                audioCapabilities: [{ contentType: 'audio/mp4;codecs="mp4a.40.2"', encryptionScheme: 'cbcs', robustness: 'SW_SECURE_CRYPTO' }],
                videoCapabilities: [{ contentType: 'video/mp4;codecs="avc1.42E01E"', encryptionScheme: 'cbcs', robustness: 'SW_SECURE_DECODE' }]
              },
              {
                audioCapabilities: [{ contentType: 'audio/mp4;codecs="mp4a.40.2"', encryptionScheme: 'cbcs', robustness: 'SW_SECURE_CRYPTO' }],
                videoCapabilities: [{ contentType: 'video/mp4;codecs="avc1.42E01E"', encryptionScheme: 'cbcs', robustness: 'SW_SECURE_CRYPTO' }]
              }
            ]
            if (this.getAttribute('widevinerobustness') === 'L1') {
              widevineOptions = [
                {
                  audioCapabilities: [{ contentType: 'audio/mp4;codecs="mp4a.40.2"', encryptionScheme: 'cbcs', robustness: 'SW_SECURE_CRYPTO' }],
                  videoCapabilities: [{ contentType: 'video/mp4;codecs="avc1.42E01E"', encryptionScheme: 'cbcs', robustness: 'HW_SECURE_ALL' }]
                }
              ]
            } else if (this.getAttribute('widevinerobustness') === 'L2') {
              widevineOptions = [
                {
                  audioCapabilities: [{ contentType: 'audio/mp4;codecs="mp4a.40.2"', encryptionScheme: 'cbcs', robustness: 'SW_SECURE_CRYPTO' }],
                  videoCapabilities: [{ contentType: 'video/mp4;codecs="avc1.42E01E"', encryptionScheme: 'cbcs', robustness: 'HW_SECURE_CRYPTO' }]
                }
              ]
            } else if (this.getAttribute('widevinel3robustness') !== null) {
              widevineOptions = [
                {
                  audioCapabilities: [{ contentType: 'audio/mp4;codecs="mp4a.40.2"', encryptionScheme: 'cbcs', robustness: 'SW_SECURE_CRYPTO' }],
                  videoCapabilities: [{ contentType: 'video/mp4;codecs="avc1.42E01E"', encryptionScheme: 'cbcs', robustness: 'SW_SECURE_CRYPTO' }]
                }
              ]
            }
          }

          return navigator.requestMediaKeySystemAccess(keySystem, widevineOptions)
        }
      }
      return playerSettings
    }

    getFaiplaySettings () {
      let playerSettings = {}
      if (this.getAttribute('fairplaycertificateuri') && this.getAttribute('fairplaylicenseuri') && this.nativeEl.canPlayType('application/vnd.apple.mpegurl') && isSafari()) {
        playerSettings = {
          emeEnabled: true,
          drmSystems: {
            'com.apple.fps': {
              licenseUrl: this.getAttribute('fairplaylicenseuri'),
              serverCertificateUrl: this.getAttribute('fairplaycertificateuri')
            }
          },
          licenseXhrSetup: (xhr, url, keyContext, licenseChallenge) => {
            try {
              // const spcBase64 = uint8ToBase64(new Uint8Array(licenseChallenge));
              const spcBase64 = btoa(String.fromCharCode.apply(null, new Uint8Array(licenseChallenge)))
              xhr.open('POST', url, true)
              xhr.setRequestHeader('Content-Type', 'application/json')
              // Add any auth headers you need here, e.g. Authorization
              // xhr.setRequestHeader('Authorization', 'Bearer ...');

              // Replace body with JSON wrapper expected by backend
              const body = JSON.stringify({ spc: spcBase64 })
              // Some hls.js versions allow returning a body to send:
              // return body;
              // But to be safe, we send via send below (hls.js will send what we return/modify)
              // We'll override send by monkeypatching send — but simpler: store body on xhr for licenseResponseCallback to use.
              return body
            } catch (err) {
              console.error('licenseXhrSetup error', err)
            }
          },
          licenseResponseCallback: (xhr, url, keyContext) => {
            // const keySystem = keyContext.keySystem;
            const response = xhr.response
            if (keyContext.keySystem === 'com.apple.fps') {
              try {
                const responseObject = JSON.parse(
                  new TextDecoder().decode(response).trim()
                )
                return this.base64ToArrayBuffer(responseObject.ckc)
              } catch (error) {
                console.error(error)
              }
            }
            return response
          }
        }
      }
      return playerSettings
    }

    uInt8ArrayToString (array) {
      return String.fromCharCode.apply(null, array)
    }

    base64ToArrayBuffer (base64) {
      const binaryString = atob(base64)
      const bytes = new Uint8Array(binaryString.length)
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i)
      }
      return bytes.buffer
    }

    base64EncodeUint8Array (input) {
      return btoa(this.uInt8ArrayToString(input))
    }

    attributeChangedCallback (attrName, oldValue, newValue) {
      if (attrName !== 'src') {
        super.attributeChangedCallback(attrName, oldValue, newValue)
      }

      if (attrName === 'src' && oldValue !== newValue) {
        this.load()
      }
    }

    #destroy () {
      if (this.api) {
        this.api.detachMedia()
        this.api.destroy()
        this.api = null
      }
    }

    // Fairplay functions
    onerror (event) {
      const { target, type } = event

      if (target) {
        // Check if the error is a MediaError
        if (target.error) {
          console.error('MediaError code:', target.error.code)
          console.error('MediaError message:', target.error.message)
        } else {
          console.error('Error: No MediaError object available.')
        }

        // Log additional details from the target element
        console.error('Target element:', target)
        console.error('Current source:', target.currentSrc)
        console.error('Ready state:', target.readyState)
        console.error('Network state:', target.networkState)
        console.error('Buffered ranges:', target.buffered)

        // Log current time and duration for context
        console.error('Current time:', target.currentTime)
        console.error('Duration:', target.duration)

        // Capture network-related details
        const videoTag = target.tagName.toLowerCase()
        if (videoTag === 'video' || videoTag === 'audio') {
          const sourceTags = target.getElementsByTagName('source')
          for (let i = 0; i < sourceTags.length; i++) {
            console.error(`Source ${i}:`, sourceTags[i].src, sourceTags[i].type)
          }

          // Log any potential CORS or network issues
          if (target.networkState === target.NETWORK_NO_SOURCE) {
            console.error('No source available for playback.')
          } else if (target.networkState === target.NETWORK_LOADING) {
            console.error('Media is currently loading.')
          } else if (target.networkState === target.NETWORK_IDLE) {
            console.error('Media is idle.')
          }
        }
      } else {
        console.error('Error: No target element available.')
      }

      // Log the type of event for additional context
      console.error('Event type:', type)

      // Capture browser and environment details for debugging
      console.error('User agent:', navigator.userAgent)

      window.console.error('A video playback error occurred!!')
    }

    stringToArray (string) {
      const buffer = new ArrayBuffer(string.length * 2) // 2 bytes for each char
      const array = new Uint16Array(buffer)
      for (let i = 0, strLen = string.length; i < strLen; i++) {
        array[i] = string.charCodeAt(i)
      }
      return array
    }

    arrayToString (array) {
      const uint16array = new Uint16Array(array.buffer)
      return String.fromCharCode.apply(null, uint16array)
    }

    extractContentId (initData) {
      let contentId = this.arrayToString(initData)
      contentId = contentId.split('skd://')[1]
      return contentId
    }

    concatInitDataIdAndCertificate (initData, id, cert) {
      if (typeof id === 'string') { id = this.stringToArray(id) }
      // layout is [initData][4 byte: idLength][idLength byte: id][4 byte:certLength][certLength byte: cert]
      let offset = 0
      const buffer = new ArrayBuffer(initData.byteLength + 4 + id.byteLength + 4 + cert.byteLength)
      const dataView = new DataView(buffer)

      const initDataArray = new Uint8Array(buffer, offset, initData.byteLength)
      initDataArray.set(initData)
      offset += initData.byteLength

      dataView.setUint32(offset, id.byteLength, true)
      offset += 4

      const idArray = new Uint16Array(buffer, offset, id.length)
      idArray.set(id)
      offset += idArray.byteLength

      dataView.setUint32(offset, cert.byteLength, true)
      offset += 4

      const certArray = new Uint8Array(buffer, offset, cert.byteLength)
      certArray.set(cert)

      return new Uint8Array(buffer, 0, buffer.byteLength)
    }

    async load () {
      this.#destroy()

      if (!this.src) {
        return
      }
      
      if (Hls.isSupported()) {
        window.Hls = Hls

        let playerSettings = {
          debug: false,
          capLevelToPlayerSize: true,
          autoStartLoad: this.nativeEl.preload,
          // Mimic the media element with an Infinity duration for live streams.

          liveDurationInfinity: true,
          enableWebVTT: false, // being handled from media chrome text tracks otherwise it shows up twice
          maxMaxBufferLength: 120,
          maxBufferSize: 30 * 1000 * 1000
        }

        await Promise.resolve()

        if (this.getAttribute('organizationid') && (this.getAttribute('organizationid') === '689e45cbc50191d6b1132597' || this.getAttribute('organizationid') === '68b6fa38c8fedf0d357af63e')) {
          // HACK FOR EGYPT CLIENT
          playerSettings.maxMaxBufferLength = 60
        }

        setMediaMetadata(this.getAttribute('videotitle'), this.getAttribute('poster'))

        // const widevineSupported = await this.checkDrmSystemSupport("com.widevine.alpha");

        playerSettings = { ...playerSettings, ...this.getWidevineSettings(), ...this.getFaiplaySettings() }

        this.api = new Hls(playerSettings)

        window.hlsPlayer = this.api
        window.videoTag = this.nativeEl

        const insightSettings = this.getAttribute('insightsettings')
        const playerJSSettings = this.getAttribute('playerjssettings')
        const ctaConfig = this.getAttribute('ctadata') ? JSON.parse(this.getAttribute('ctadata')) : []
        const imageOverlayConfig = this.getAttribute('imageoverlay') ? JSON.parse(this.getAttribute('imageoverlay')) : null

        if (insightSettings) {
          insightsInit(
            JSON.parse(insightSettings),
            'hls',
            this.nativeEl,
            this.api,
            new Date().getTime()
          )
        }

        /* eslint-disable no-new */
        new HTML5EventsAdapter(
          this.nativeEl,
          JSON.parse(playerJSSettings),
          ctaConfig,
          imageOverlayConfig,
          this.getAttribute('t'),
          this.getAttribute('mu') === 'true',
          this.getAttribute('isBackgroundOnly') === 'true',
          this.getAttribute('resumewhereleft') === 'true',
          this.getAttribute('areplayercontrolsdisabled') === 'true',
          this.getAttribute('captionlanguage'),
          this.getAttribute('widgetsettings'),
          this.getAttribute('captionsoffonload') && this.getAttribute('captionsoffonload') === 'true'
        )

        // Wait 1 tick to allow other attributes to be set.
        await Promise.resolve()

        // Set up preload

        switch (this.nativeEl.preload) {
          case 'none': {
            // when preload is none, load the source on first play
            const loadSourceOnPlay = () => this.api.loadSource(this.src)
            this.nativeEl.addEventListener('play', loadSourceOnPlay, {
              once: true
            })
            this.api.on(Hls.Events.DESTROYING, () => {
              this.nativeEl.removeEventListener('play', loadSourceOnPlay)
            })
            break
          }
          case 'metadata': {
            const originalLength = this.api.config.maxBufferLength
            const originalSize = this.api.config.maxBufferSize
            // load the least amount of data possible
            this.api.config.maxBufferLength = 1
            this.api.config.maxBufferSize = 1
            // and once a user has player, allow for it to load data as normal
            const increaseBufferOnPlay = () => {
              this.api.config.maxBufferLength = originalLength
              this.api.config.maxBufferSize = originalSize
            }
            this.nativeEl.addEventListener('play', increaseBufferOnPlay, {
              once: true
            })
            this.api.on(Hls.Events.DESTROYING, () => {
              this.nativeEl.removeEventListener('play', increaseBufferOnPlay)
            })
            this.api.loadSource(this.src)
            break
          }
          default:
            // load source immediately for any other preload value
            this.api.loadSource(this.src)
        }

        this.api.attachMedia(this.nativeEl)

        // // Event listeners for cast debugging
        // this.remote.addEventListener('connecting', function (event) {
        //   // console.log("Cast connecting", event);
        // })

        // this.remote.addEventListener('connect', function (event) {
        //   // console.log("Cast connect", event);
        // })

        // this.remote.addEventListener('disconnect', function (event) {
        //   // console.log("Cast disconnect", event);
        // })

        // this.remote.watchAvailability((available) => {
        //   const castBtn = document.getElementById('castBtn')
        //   castBtn.hidden = !available
        // })

        this.api.on(Hls.Events.ERROR, (event, data) => {
          const { type, details, fatal } = data
          if (fatal) {
            switch (type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                if (
                  details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR &&
                  (
                    data.networkDetails && [
                      'Token is required to access this URL',
                      'Expiration is required to access this URL',
                      'Invalid token format',
                      'Invalid expiration format',
                      'Token expired',
                      'Token signature does not match'
                    ].indexOf(data.networkDetails.response) !== -1
                  )
                ) {
                  document.querySelector('.fatal-error-overlay').style.display = 'flex'
                  document.querySelector('.fatal-error-type').textContent = 'Invalid Video Link'
                  document.querySelector('.fatal-error-message').textContent = 'Please try again with a new video link.'
                } else {
                  this.api.startLoad() // Attempt to reload the stream
                }
                break
              case Hls.ErrorTypes.MEDIA_ERROR:
                this.api.recoverMediaError() // Try recovering from media errors
                break
              default:
                if (details === 'keySystemNoAccess') {
                  document.querySelector('.fatal-error-heading').textContent = 'Oops! Secure Video Found'
                  document.querySelector('.fatal-error-type').textContent = 'Your browser doesn\'t support playing secure videos. Please try from a different browser or device.'
                  document.querySelector('.fatal-error-message').textContent = ''
                  document.querySelector('.fatal-error-overlay').style.display = 'flex'
                } else if (details === 'keySystemStatusOutputRestricted') {
                  document.querySelector('.fatal-error-heading').textContent = 'Oops! Secure Video Found'
                  document.querySelector('.fatal-error-type').textContent = data.error
                  document.querySelector('.fatal-error-message').textContent = ''
                  document.querySelector('.fatal-error-overlay').style.display = 'flex'
                } else if (details === 'internalException' && data.error.code === 11 && this.getAttribute('src').includes('token') && this.getAttribute('src').includes('expires')) {
                  // show an error with reload button if showreloadbuttonontokenexpiry is true
                  document.querySelector('.fatal-error-heading').textContent = 'Oops! Secure Session has Expired'
                  if (this.getAttribute('showreloadbuttonontokenexpiry') === 'true') {
                    document.querySelector('.fatal-error-message').textContent = ''
                    document.querySelector('.fatal-error-message').insertAdjacentHTML('beforeend', '<button onClick="window.location.reload()" class="bg-gray-900 dark:text-gray-900 text-white dark:bg-gray-100 rounded text-sm px-4 py-1 mt-1">Reload</button>')
                  } else {
                    document.querySelector('.fatal-error-message').textContent = ''
                  }
                  document.querySelector('.fatal-error-overlay').style.display = 'flex'
                } else if (details === 'internalException' && this.getAttribute('refreshoninternalexception') === 'true') {
                  // window.location.reload()
                  // recover media allows to recover from media errors
                  // we are not showing an overlay because video will resume playing
                  if(this.attemptedErrorRecoveryCount === undefined || this.attemptedErrorRecoveryCount < 6) {
                    this.api.recoverMediaError()
                    this.attemptedErrorRecoveryCount = (this.attemptedErrorRecoveryCount || 0) + 1;
                  }else{
                    document.querySelector('.fatal-error-heading').textContent = 'Oops! Something has gone wrong'
                    document.querySelector('.fatal-error-type').textContent = `Type: mediaChunkFailed`
                    document.querySelector('.fatal-error-message').textContent = `Code: ${details}`
                    document.querySelector('.fatal-error-overlay').style.display = 'flex'
                  }
                } else {
                  document.querySelector('.fatal-error-heading').textContent = 'Oops! Something has gone wrong'
                  document.querySelector('.fatal-error-type').textContent = `Type: ${type}`
                  document.querySelector('.fatal-error-message').textContent = `Code: ${details}`
                  document.querySelector('.fatal-error-overlay').style.display = 'flex'
                }

                console.error(event, data)
                break
            }
          }
        })

        // Set up tracks & renditions

        // Create a map to save the unique id's we create for each level and rendition.
        // hls.js uses the levels array index primarily but we'll use the id to have a
        // 1 to 1 relation from rendition to level.
        const levelIdMap = new WeakMap()

        this.api.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
          // Remove all existing media tracks
          removeAllMediaTracks()

          // Handle video tracks
          let videoTrack = this.videoTracks.getTrackById('main')
          if (!videoTrack) {
            videoTrack = this.addVideoTrack('main')
            videoTrack.id = 'main'
            videoTrack.selected = true
          }

          // Map to store level IDs
          const levelIdMap = new WeakMap()

          // Widevine doesn't supports hvc1 codec, so we need to remove it from the renditions
          const removeIndexList = []
          // Only remove hvc1 codec renditions if widevine is enabled
          if (this.getAttribute('widevinelicenseuri') !== null) {
            data.levels.forEach((level, levelIndex) => {
              if (level.codecSet.startsWith('hvc1')) {
                removeIndexList.push(levelIndex)
              }
            })
          }

          removeIndexList.reverse().forEach((level) => {
            this.api.removeLevel(level)
          })
          // Get available qualities
          // const availableQualities = data.levels.map(level => Math.min(level.width, level.height))

          const filteredLevels = []
          const filteredLevelsMap = new Map()
          for (const [id, level] of data.levels.entries()) {
            // check if the level height and width does not exists in the filteredLevelsMap
            const levelKey = level.height + '_' + level.width
            if (!filteredLevelsMap.has(levelKey)) {
              filteredLevelsMap.set(levelKey, id)
              filteredLevels.push({
                level,
                id
              })
            }
          };

          filteredLevels.forEach((qualityLevel) => {
            const videoRendition = videoTrack.addRendition(
              qualityLevel.level.url[0],
              qualityLevel.level.width,
              qualityLevel.level.height,
              qualityLevel.level.videoCodec,
              qualityLevel.level.bitrate
            )

            // Save the id in a WeakMap
            levelIdMap.set(qualityLevel.level, `${qualityLevel.id}`)
            videoRendition.id = `${qualityLevel.id}`
          })

          // Handle audio tracks
          let isDesiredAudioTrackFound = false
          for (const [id, a] of data.audioTracks.entries()) {
            const kind = a.default ? 'main' : 'alternative'
            const audioTrack = this.addAudioTrack(kind, a.name, a.lang)
            audioTrack.id = `${id}`

            if (this.getAttribute('audiotracklang') && this.getAttribute('audiotracklang') === a.lang && !isDesiredAudioTrackFound) {
              audioTrack.enabled = true
              isDesiredAudioTrackFound = true
            } else if (a.default && !isDesiredAudioTrackFound) {
              audioTrack.enabled = true
            }
          };

          if (this.getAttribute('startHighestRes') && this.getAttribute('startHighestRes') === 'true') {
            this.api.startLevel = filteredLevels[filteredLevels.length - 1].id
          } else {
            // if -1: automatic start level selection, playback will start from level matching download bandwidth (determined from download of first segment).
            this.api.startLevel = -1
          }
        })

        this.audioTracks.addEventListener('change', () => {
          // Cast to number, hls.js uses numeric id's.
          const audioTrackId = +[...this.audioTracks].find(t => t.enabled)?.id
          const availableIds = this.api.audioTracks.map(t => t.id)
          if (audioTrackId !== this.api.audioTrack && availableIds.includes(audioTrackId)) {
            this.api.audioTrack = audioTrackId
          }
        })

        // Fired when a level is removed after calling `removeLevel()`
        this.api.on(Hls.Events.LEVELS_UPDATED, (event, data) => {
          const videoTrack = this.videoTracks[this.videoTracks.selectedIndex ?? 0]
          if (!videoTrack) return

          const levelIds = data.levels.map((l) => levelIdMap.get(l))

          for (const rendition of this.videoRenditions) {
            if (rendition.id && !levelIds.includes(rendition.id)) {
              videoTrack.removeRendition(rendition)
            }
          }
        })

        // hls.js doesn't support enabling multiple renditions.
        //
        // 1. if all renditions are enabled it's auto selection.
        // 2. if 1 of the renditions is disabled we assume a selection was made
        //    and lock it to the first rendition that is enabled.
        const switchRendition = (event) => {
          const level = event.target.selectedIndex

          if (event.target && event.target[level] && event.target[level].id) {
            const newSelectedLevel = parseInt(event.target[level].id)
            if (this.api.currentLevel !== newSelectedLevel && this.api.levels[newSelectedLevel]) {
              smoothSwitch(newSelectedLevel)
            };
          }
        }

        // Workaround for issue changing renditions on an alternative audio track.
        // https://github.com/video-dev/hls.js/issues/5749#issuecomment-1684629437

        // const smoothSwitch = (levelIndex) => {
        //   const currentTime = this.currentTime;
        //   let flushedFwdBuffer = false;

        //   const callback = (event, data) => {
        //     flushedFwdBuffer ||= !Number.isFinite(data.endOffset);
        //   };

        //   this.api.on(Hls.Events.BUFFER_FLUSHING, callback);
        //   this.api.currentLevel = levelIndex;
        //   this.api.off(Hls.Events.BUFFER_FLUSHING, callback);

        //   if (!flushedFwdBuffer) {
        //     this.api.trigger(Hls.Events.BUFFER_FLUSHING, {
        //       startOffset: currentTime + 10,
        //       endOffset: Infinity,
        //       type: 'video',
        //     });
        //   }
        // };

        const smoothSwitch = (levelIndex) => {
          const video = this // Assuming 'this' refers to the video element
          const currentTime = video.currentTime
          const originalyPaused = video.paused
          let flushedFwdBuffer = false

          // Pause the video
          const overlay = window.document.getElementsByClassName('quality-switcher-overlay')
          if (overlay.length > 0) {
            overlay[0].style.display = 'block'
          }
          video.pause()
          // hide the media-play-button
          const btnPause = document.querySelector('#center-button media-play-button')
          if (btnPause) {
            btnPause.classList.add('hidden')
          }

          // Callback to monitor BUFFER_FLUSHING events
          const callback = (event, data) => {
            if (!Number.isFinite(data.endOffset)) {
              flushedFwdBuffer = true
            }
          }

          // Listen for buffer flushing events
          this.api.on(Hls.Events.BUFFER_FLUSHING, callback)

          // Change the quality level
          this.api.currentLevel = levelIndex

          // Trigger manual buffer flushing if the forward buffer hasn't been flushed
          if (!flushedFwdBuffer) {
            this.api.trigger(Hls.Events.BUFFER_FLUSHING, {
              startOffset: currentTime + 10, // Flush buffers ahead of the current playback
              endOffset: Infinity,
              type: 'video'
            })
          }

          // Listen for BUFFER_APPENDED to detect when new chunks are loaded
          const onBufferAppended = () => {
            overlay[0].style.display = 'none'
            btnPause.classList.remove('hidden')
            // Resume playback when buffering is complete
            video.currentTime = currentTime // Sync to the correct playback position
            if (!originalyPaused) {
              video.play()
            }

            // Clean up event listeners
            this.api.off(Hls.Events.BUFFER_FLUSHING, callback)
            this.api.off(Hls.Events.BUFFER_APPENDED, onBufferAppended)
          }

          this.api.on(Hls.Events.BUFFER_APPENDED, onBufferAppended)

          // Fallback: In case BUFFER_APPENDED is missed, resume playback after a delay
          // setTimeout(() => {
          //   if (video.paused) {
          //     overlay[0].style.display = 'none';
          //     video.currentTime = currentTime;
          //     if(!originalyPaused && video.paused){
          //       video.play();
          //     }
          //     this.api.off(Hls.Events.BUFFER_FLUSHING, callback);
          //     this.api.off(Hls.Events.BUFFER_APPENDED, onBufferAppended);
          //   }
          // }, 3000); // Adjust timeout as necessary
        }

        this.videoRenditions?.addEventListener('change', switchRendition)

        const removeAllMediaTracks = () => {
          for (const videoTrack of this.videoTracks) {
            this.removeVideoTrack(videoTrack)
          }
          for (const audioTrack of this.audioTracks) {
            this.removeAudioTrack(audioTrack)
          }
        }

        this.api.once(Hls.Events.DESTROYING, removeAllMediaTracks)

        return
      } else if (this.nativeEl.canPlayType('application/vnd.apple.mpegurl') && isSafari()) {
        // const fairplaySupported = await this.checkDrmSystemSupport("com.apple.fps");
        await Promise.resolve()

        setMediaMetadata(this.getAttribute('videotitle'), this.getAttribute('poster'))

        const insightSettings = this.getAttribute('insightsettings')
        const playerJSSettings = this.getAttribute('playerjssettings')
        const ctaConfig = this.getAttribute('ctadata') ? JSON.parse(this.getAttribute('ctadata')) : []
        const imageOverlayConfig = this.getAttribute('imageoverlay') ? JSON.parse(this.getAttribute('imageoverlay')) : null

        if (this.getAttribute('fairplaylicenseuri') !== null && this.getAttribute('fairplaycertificateuri') !== null) {
          const response = await fetch(this.getAttribute('fairplaycertificateuri'))

          window.fairplayCertificate = await response.arrayBuffer()

          this.nativeEl.addEventListener('error', this.onerror, false)

          this.nativeEl.addEventListener('webkitneedkey', async (event) => {
            try {
              // const initDataType = event.initDataType

              const video = event.target
              let mediaKeySystem = 'com.apple.fps.1_0'
              if ('MediaSource' in window && navigator.maxTouchPoints === 0) {
                mediaKeySystem = 'com.apple.fps'
              }

              let initData = event.initData

              const contentId = this.extractContentId(initData)

              initData = this.concatInitDataIdAndCertificate(initData, contentId, new Uint8Array(window.fairplayCertificate))

              const keys = new window.WebKitMediaKeys(mediaKeySystem)

              if (!video.webkitKeys) { await video.webkitSetMediaKeys(keys) }

              const session = video.webkitKeys.createSession('video/mp4', initData)

              session.contentId = contentId

              session.addEventListener('webkitkeyerror', (event) => {
                console.error('webkitkeyerror', event)
              })
              // session.addEventListener('webkitkeyadded', (event) => {
              //   console.log("webkitkeyadded", event)
              // });

              const message = await new Promise(resolve => {
                session.addEventListener('webkitkeymessage', resolve, { once: true })
              })

              const spcString = btoa(String.fromCharCode.apply(null, new Uint8Array(message.message)))
              const licenseResponse = await fetch(this.getAttribute('fairplaylicenseuri'), {
                method: 'POST',
                headers: new Headers({ 'Content-type': 'application/json' }),
                body: JSON.stringify({
                  spc: spcString
                })
              })
              const responseObject = await licenseResponse.json()
              const response = Uint8Array.from(atob(responseObject.ckc), c => c.charCodeAt(0))

              await session.update(response)
              return session
            } catch (e) {
              console.error(e)
              window.console.error(`Could not start encrypted playback due to exception "${e}"`)
            }
          })

          this.nativeEl.src = ''
          this.nativeEl.src = this.src
        } else if (this.getAttribute('widevinelicenseuri') !== null) {
          console.error('Fairplay License URI or Certificate URI not provided')
          document.querySelector('.fatal-error-overlay').style.display = 'flex'
          document.querySelector('.fatal-error-heading').textContent = 'Oops! Secure Video Found'
          document.querySelector('.fatal-error-type').textContent = 'To play this video, switch to a different browser, like Google Chrome, Firefox, or MS Edge.'
          document.querySelector('.fatal-error-message').textContent = ''
        } else {
          this.nativeEl.src = ''
          this.nativeEl.src = this.src
        }

        window.videoTag = this.nativeEl

        if (insightSettings) {
          insightsInit(
            JSON.parse(insightSettings),
            'html5',
            this.nativeEl,
            null,
            new Date().getTime()
          )
        }

        /* eslint-disable no-new */
        new HTML5EventsAdapter(
          this.nativeEl,
          JSON.parse(playerJSSettings),
          ctaConfig,
          imageOverlayConfig,
          this.getAttribute('t'),
          this.getAttribute('mu') === 'true',
          this.getAttribute('isBackgroundOnly') === 'true',
          this.getAttribute('resumewhereleft') === 'true',
          this.getAttribute('areplayercontrolsdisabled') === 'true',
          this.getAttribute('captionlanguage'),
          this.getAttribute('widgetsettings'),
          this.getAttribute('captionsoffonload') && this.getAttribute('captionsoffonload') === 'true'
        )
      }
      console.error('No way to play this video!!')
      await Promise.resolve()
    }
  }
}

const HlsVideoElement = CastableMediaMixin(HlsVideoMixin(MediaTracksMixin(CustomVideoElement)))

if (globalThis.customElements && !globalThis.customElements.get('hls-video')) {
  globalThis.customElements.define('hls-video', HlsVideoElement)
}

// https://github.com/DevLARLEY/WidevineProxy2 this function is defined by this malicious plugin
// we just neutralise it.
if (globalThis.emitAndWaitForResponse) {
  globalThis.emitAndWaitForResponse = (arg1, arg2) => {}
}

export default HlsVideoElement

export {
  Hls,
  HlsVideoMixin,
  HlsVideoElement
}
